import React from "react"
import { Link } from "react-router-dom"
import { Button, Grid, Box, Typography } from "@mui/material"
import CarIcon from "@mui/icons-material/DirectionsCar"
import { useTheme } from "@mui/material/styles"
import { CloudinaryImage } from "@cloudinary/url-gen"
import { AdvancedImage } from "@cloudinary/react"
import { crop } from "@cloudinary/url-gen/actions/resize"

import LeadBlock from "../template-parts/product-marketing/LeadBlock"
import Section from "../template-parts/product-marketing/Section"
import { stateMarketing } from "../../content"
import RoundedCard from "../template-parts/product-marketing/RoundedCard"
import DriversEdMenuPopover from "../template-parts/DriversEdMenuPopover"
import DrivingLessonMenuPopover from "../template-parts/DrivingLessonMenuPopover"
import { lazyLoadPreset } from "../../utils/cloudinaryImageUtils"

import arrowIcon from "../../assets/images/homepage-arrow.svg"

const IMAGES = [
  "sparklers_zmoqdn",
  "happy-woods-square_ewzr1w",
  "happy-friends-smaller-square_iwuo5g",
  "happy-driver-smaller-square_kx7sn9",
]

type Props = {
  title: string | object
  subhead?: string
  link?: string
  intro?: string
  showDriversEd?: boolean
  isHomePage?: boolean
}

export const BuyNowPayLaterBubble = () => {
  const theme = useTheme()

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        mt: 1,
        ml: 1,
        maxWidth: 240,
      }}
      className="driving-lesson-financing-bubble"
    >
      <Box>
        <img
          src={arrowIcon}
          alt="Arrow"
          style={{
            width: 24,
            height: 24,
            transform: "rotate(90deg)",
          }}
        />
      </Box>
      <Box
        sx={{
          backgroundColor: theme.palette.accentOne.main,
          color: "#ffffff",
          fontWeight: "bold",
          fontSize: "0.9em",
          padding: 1,
          pl: 2,
          pr: 2,
          borderRadius: 4,
          marginTop: 1,
        }}
      >
        Financing available!
      </Box>
    </Box>
  )
}

const GenericLandingHero = ({
  title,
  subhead,
  intro = stateMarketing.ca.landingPageIntro,
  link = "/location-search",
  showDriversEd = true,
  isHomePage = false,
}: Props) => {
  const theme = useTheme()

  return (
    <Section className="landing-page hero-section">
      <Box sx={{ display: "flex", margin: "0 auto", maxWidth: 1400 }}>
        <Box
          sx={{
            p: {
              xs: 4,
              sm: 6,
              md: 8,
            },
            paddingRight: {
              md: 2,
              lg: 4,
            },
            flexGrow: 1,
            maxWidth: showDriversEd ? 800 : 600,
          }}
        >
          {isHomePage ? (
            <Box>
              <Typography
                sx={{
                  fontSize: "1.2rem",
                  fontWeight: "bold",
                  color: theme.palette.accentThree.main,
                  textTransform: "uppercase",
                }}
              >
                Coastline Driving School
              </Typography>
            </Box>
          ) : null}
          <LeadBlock
            head={title}
            subhead={subhead}
            text={!isHomePage ? intro : undefined}
          >
            <RoundedCard
              style={{
                borderRadius: 0,
                borderLeft: `${theme.palette.secondary.main} 4px solid`,
              }}
            >
              <p style={{ marginTop: 0 }}>
                Get started by choosing in-car lessons
                {showDriversEd ? " or an online course" : ""}:
              </p>
              <Grid container spacing={2}>
                <Grid item>
                  <DrivingLessonMenuPopover />
                  <Button
                    component={Link}
                    to={link}
                    variant="contained"
                    className="button-cta hidden"
                  >
                    <CarIcon style={{ marginRight: 10 }} />
                    Driving Lessons
                  </Button>
                  <BuyNowPayLaterBubble />
                </Grid>
                {showDriversEd && (
                  <Grid item>
                    <DriversEdMenuPopover />
                  </Grid>
                )}
              </Grid>
            </RoundedCard>
          </LeadBlock>
        </Box>
        <Box
          className="hide-mobile"
          display="block"
          alignSelf="center"
          sx={{ flexShrink: 0, width: 650 }}
        >
          <AdvancedImage
            cldImg={new CloudinaryImage(
              `hero-images/${IMAGES[Math.floor(Math.random() * IMAGES.length)]}`,
              { cloudName: "coastline-academy" },
            )
              .resize(crop())
              .quality(80)
              .format("auto")}
            alt="Coastline Academy"
            style={{
              padding: 60,
              borderRadius: 160,
              maxWidth: 650,
            }}
            plugins={[lazyLoadPreset]}
          />
        </Box>
      </Box>
    </Section>
  )
}

export default GenericLandingHero
